import React from 'react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import type { Document } from '@contentful/rich-text-types';

import { BUTTON_SIZES, PrimaryButton } from 'js/src/components/common/ui/Button';
import ContentfulImageOrVideo from 'js/src/components/public-pages/ContentfulImageOrVideo';
import type { ImmutableMap } from 'js/src/libs/consts';
import type { ContentfulImageOrVideoData } from 'js/src/libs/contentful-utils';
import { getColClassNames, TextWidth } from 'js/src/libs/contentful-utils';

type Props = {
    data: ImmutableMap<{
        paragraph: Document;
        headline: string;
        image: ContentfulImageOrVideoData;
        image_side: string;
        cta_url?: string;
        cta_text?: string;
        text_width?: TextWidth;
    }>;
    bgColor: string;
    lazyLoad: boolean;
};

const ImageAndText = React.memo(function ImageAndText({ data, bgColor, lazyLoad }: Props) {
    let paragraphNode = null;
    if (data.get('paragraph')) {
        paragraphNode = documentToReactComponents(data.get('paragraph').toJS());
    }

    let headlineNode = null;
    if (data.get('headline')) {
        headlineNode = <h3>{data.get('headline')}</h3>;
    }

    let cta = null;

    const ctaUrl = data.get('cta_url');
    const ctaText = data.get('cta_text');

    if (ctaUrl && ctaText) {
        cta = <PrimaryButton href={ctaUrl} size={BUTTON_SIZES.LG} text={ctaText} />;
    }
    const textNode = (
        <>
            {headlineNode}
            {paragraphNode}
            {cta && <div className="mt-l5-24">{cta}</div>}
        </>
    );
    const imageNode = (
        <ContentfulImageOrVideo
            data={data.get('image')}
            urlWidth={600}
            style={{ width: '100%' }}
            lazyLoad={lazyLoad}
        />
    );

    const isImageLeft = (data.get('image_side') || '').toString().toLowerCase() === 'left';

    let leftNode = textNode;
    let rightNode = imageNode;
    if (isImageLeft) {
        leftNode = imageNode;
        rightNode = textNode;
    }

    const textWidth = data.get('text_width') || TextWidth.STANDARD;
    const { leftMd, rightMd } = getColClassNames(textWidth, isImageLeft);

    return (
        <div className="contentful-component image-and-text" style={{ backgroundColor: bgColor }}>
            <div className="container">
                <div className="row">
                    <div className={`${leftMd} col-sm-12 align-self-center my-l5-8`}>
                        <div className="me-l5-48">{leftNode}</div>
                    </div>
                    <div className={`${rightMd} col-sm-12 align-self-center my-l5-8`}>
                        <div className="ms-l5-48">{rightNode}</div>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default ImageAndText;
